import { LanguagesQuery } from '../../generated/hooks';
import { EMPLOYEE_COLUMNS, ImportTab, TEAM_COLUMNS } from './constants';
import { EmployeeRecord, TeamRecord, ValidationError } from './OrganizationImport';
import { isEmployeeEmpty } from './utils';

const isValidEmail = (email: string) =>
  /^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?$/.test(
    email.toLowerCase(),
  );

// New function to validate phone numbers specifically
export const validatePhoneNumbers = (
  employees: EmployeeRecord[],
  verifiedPhoneNumbers: string[],
): ValidationError[] => {
  const validationErrors: ValidationError[] = [];

  employees.forEach((employee, index) => {
    if (isEmployeeEmpty(employee)) {
      return; // Skip empty rows
    }

    if (employee.phoneNumber) {
      // Create the full phone number with country code
      const fullPhoneNumber = `+${employee.countryCallingCode}${employee.phoneNumber}`.replace('++', '+');

      // Check if the phone number was verified
      if (!verifiedPhoneNumbers.includes(fullPhoneNumber)) {
        validationErrors.push({
          position: [{ row: index, column: EMPLOYEE_COLUMNS.phoneNumber }],
          message: 'invalidPhoneNumber',
          messageObject: { phone: fullPhoneNumber },
          tab: ImportTab.EMPLOYEES,
        });
      }
    }
  });

  return validationErrors;
};

const findCycleEndpointsWithIndices = (
  teams: { name: string; supervisorTeam: string }[],
): { first: { name: string; index: number }; last: { name: string; index: number } }[] => {
  const graph: Record<string, string[]> = {};
  const visited: Set<string> = new Set();
  const stack: { name: string; index: number }[] = [];
  const cycles: { first: { name: string; index: number }; last: { name: string; index: number } }[] = [];

  // Create graph
  teams.forEach(({ name, supervisorTeam }) => {
    if (!graph[name]) graph[name] = [];
    if (supervisorTeam) {
      graph[name].push(supervisorTeam);
    }
  });

  // DFS to find cycles
  const dfs = (node: { name: string; index: number }) => {
    if (stack.some((item) => item.name === node.name)) {
      // Cycle detected
      const cycleStartIndex = stack.findIndex((item) => item.name === node.name);
      const first = stack[cycleStartIndex];
      const last = stack[stack.length - 1];
      cycles.push({ first, last });
      return;
    }
    if (visited.has(node.name)) return; // Already visited

    visited.add(node.name);
    stack.push(node);

    for (const neighbor of graph[node.name] || []) {
      const neighborIndex = teams.findIndex((team) => team.name === neighbor);
      if (neighborIndex !== -1) {
        dfs({ name: neighbor, index: neighborIndex });
      }
    }

    stack.pop();
  };

  teams.forEach((team, index) => {
    dfs({ name: team.name, index });
  });

  return cycles;
};

export const validate = (employees: EmployeeRecord[], teams: TeamRecord[], languages?: LanguagesQuery['languages']) => {
  const validationErrors: ValidationError[] = [];
  const emailOccurrences = new Map<string, number>();
  const phoneOccurrences = new Map<string, number>();
  const teamOccurrences = new Map<string, number>();

  employees.forEach((employee) => {
    if (employee.email) {
      emailOccurrences.set(employee.email, (emailOccurrences.get(employee.email) || 0) + 1);
    }
    if (employee.otherEmail) {
      emailOccurrences.set(employee.otherEmail, (emailOccurrences.get(employee.otherEmail) || 0) + 1);
    }
    if (employee.phoneNumber) {
      phoneOccurrences.set(employee.phoneNumber, (phoneOccurrences.get(employee.phoneNumber) || 0) + 1);
    }
  });

  teams.forEach((team) => {
    if (team.name) {
      teamOccurrences.set(team.name, (teamOccurrences.get(team.name) || 0) + 1);
    }
  });

  teamOccurrences.forEach((count, name) => {
    if (count > 1) {
      validationErrors.push({
        position: teams
          .map((team, index) => {
            if (team.name === name) {
              return { row: index, column: TEAM_COLUMNS.name };
            } else {
              return null;
            }
          })
          .filter(Boolean) as { row: number; column: number }[],
        message: 'duplicateTeamName',
        messageObject: { name },
        tab: ImportTab.TEAMS,
      });
    }
  });

  // Check for conflicts between team IDs and names across different teams
  teams.forEach((team, index) => {
    if (team.name) {
      // Check if any other team has this team's name as their ID
      const conflictingIndex = teams.findIndex(
        (otherTeam, otherIndex) => otherIndex !== index && otherTeam.id === team.name,
      );

      if (conflictingIndex !== -1) {
        validationErrors.push({
          position: [
            { row: index, column: TEAM_COLUMNS.name },
            { row: conflictingIndex, column: TEAM_COLUMNS.id },
          ],
          message: 'teamIdNameConflict',
          messageObject: { value: team.name },
          tab: ImportTab.TEAMS,
        });
      }
    }

    if (team.id) {
      // Check if any other team has this team's ID as their name
      const conflictingIndex = teams.findIndex(
        (otherTeam, otherIndex) => otherIndex !== index && otherTeam.name === team.id,
      );

      if (conflictingIndex !== -1) {
        validationErrors.push({
          position: [
            { row: index, column: TEAM_COLUMNS.id },
            { row: conflictingIndex, column: TEAM_COLUMNS.name },
          ],
          message: 'teamIdNameConflict',
          messageObject: { value: team.id },
          tab: ImportTab.TEAMS,
        });
      }
    }
  });

  emailOccurrences.forEach((count, email) => {
    if (count > 1) {
      validationErrors.push({
        position: employees
          .map((employee, index) => {
            if (employee.email === email && employee.otherEmail === email) {
              return [
                { row: index, column: EMPLOYEE_COLUMNS.email },
                { row: index, column: EMPLOYEE_COLUMNS.otherEmail },
              ];
            } else if (employee.email === email) {
              return { row: index, column: EMPLOYEE_COLUMNS.email };
            } else if (employee.otherEmail === email) {
              return { row: index, column: EMPLOYEE_COLUMNS.otherEmail };
            } else {
              return null;
            }
          })
          .flat()
          .filter(Boolean) as { row: number; column: number }[],
        message: 'duplicateEmail',
        messageObject: { email },
        tab: ImportTab.EMPLOYEES,
      });
    }
  });

  phoneOccurrences.forEach((count, phone) => {
    if (count > 1) {
      validationErrors.push({
        position: employees
          .map((employee, index) => {
            if (employee.phoneNumber === phone) {
              return { row: index, column: EMPLOYEE_COLUMNS.phoneNumber };
            } else {
              return null;
            }
          })
          .filter(Boolean) as { row: number; column: number }[],
        message: 'duplicatePhoneNumber',
        messageObject: { phone },
        tab: ImportTab.EMPLOYEES,
      });
    }
  });

  employees.forEach((employee, index) => {
    if (isEmployeeEmpty(employee)) {
      return; // Skip empty rows
    }
    if (employee.email && !(isValidEmail(employee.email) || employee.email.startsWith('#'))) {
      validationErrors.push({
        position: [{ row: index, column: EMPLOYEE_COLUMNS.email }],
        message: 'invalidEmail',
        messageObject: { email: employee.email },
        tab: ImportTab.EMPLOYEES,
      });
    }
    if (employee.otherEmail && !isValidEmail(employee.otherEmail)) {
      validationErrors.push({
        position: [
          {
            row: index,
            column: EMPLOYEE_COLUMNS.otherEmail,
          },
        ],
        message: 'invalidEmail',
        messageObject: { email: employee.otherEmail },
        tab: ImportTab.EMPLOYEES,
      });
    }
    if (employee.language && !languages?.find((lang) => lang.code === employee.language.toLowerCase())) {
      validationErrors.push({
        position: [
          {
            row: index,
            column: EMPLOYEE_COLUMNS.language,
          },
        ],
        message: 'invalidLanguage',
        messageObject: { language: employee.language },
        tab: ImportTab.EMPLOYEES,
      });
    }
    if (employee.firstname === '') {
      validationErrors.push({
        position: [
          {
            row: index,
            column: EMPLOYEE_COLUMNS.firstname,
          },
        ],
        message: 'emptyField',
        tab: ImportTab.EMPLOYEES,
      });
    }
    if (employee.surname === '') {
      validationErrors.push({
        position: [
          {
            row: index,
            column: EMPLOYEE_COLUMNS.surname,
          },
        ],
        message: 'emptyField',
        tab: ImportTab.EMPLOYEES,
      });
    }
    if (!employee.preferredContact) {
      validationErrors.push({
        position: [
          {
            row: index,
            column: EMPLOYEE_COLUMNS.preferredContact,
          },
        ],
        message: 'emptyField',
        tab: ImportTab.EMPLOYEES,
      });
    }
    if (employee.preferredContact !== '' && !['SMS', 'EMAIL'].includes(employee.preferredContact)) {
      validationErrors.push({
        position: [
          {
            row: index,
            column: EMPLOYEE_COLUMNS.preferredContact,
          },
        ],
        message: 'invalidContactType',
        messageObject: { name: employee.preferredContact },
        tab: ImportTab.EMPLOYEES,
      });
    }
    if (employee.preferredContact === 'EMAIL' && !(employee.email || employee.otherEmail)) {
      validationErrors.push({
        position: [
          {
            row: index,
            column: EMPLOYEE_COLUMNS.email,
          },
          {
            row: index,
            column: EMPLOYEE_COLUMNS.otherEmail,
          },
        ],
        message: 'oneOfFieldsRequired',
        tab: ImportTab.EMPLOYEES,
      });
    }
    if (employee.preferredContact === 'SMS' && !employee.phoneNumber) {
      validationErrors.push({
        position: [
          {
            row: index,
            column: EMPLOYEE_COLUMNS.phoneNumber,
          },
        ],
        message: 'emptyField',
        tab: ImportTab.EMPLOYEES,
      });
    }
    if (employee.team !== '' && !teams.find((team) => team.name === employee.team || team.id === employee.team)) {
      validationErrors.push({
        position: [
          {
            row: index,
            column: EMPLOYEE_COLUMNS.team,
          },
        ],
        message: 'teamNotFound',
        messageObject: { name: employee.team },
        tab: ImportTab.EMPLOYEES,
      });
    }
    if (
      employee.managerOfTeam.length > 0 &&
      !employee.managerOfTeam.every((manager) => teams.find((team) => team.name === manager || team.id === manager))
    ) {
      // Create separate validation errors for each invalid team
      employee.managerOfTeam.forEach((manager) => {
        if (!teams.find((team) => team.name === manager || team.id === manager)) {
          validationErrors.push({
            position: [{ row: index, column: EMPLOYEE_COLUMNS.managerOfTeam }],
            message: 'teamNotFound',
            messageObject: { name: manager },
            tab: ImportTab.EMPLOYEES,
          });
        }
      });
    }
  });

  const teamsWithMultipleEmployeesWithSameManager = teams.filter((team) => {
    if (team.name === '') {
      return false;
    }
    const employeesWithSameManager = employees.filter(
      (employee) =>
        employee.managerOfTeam.includes(team.name) ||
        (employee.managerOfTeam.includes(team.id) && !!employee.managerOfTeam.find((m) => m === team.id)),
    );
    return employeesWithSameManager.length > 1;
  });

  teamsWithMultipleEmployeesWithSameManager.forEach((team, index) => {
    const employeesIndexes = employees
      .map((employee, index) => {
        return employee.managerOfTeam.includes(team.name) || employee.managerOfTeam.includes(team.id) ? index : null;
      })
      .filter(Boolean) as number[];
    validationErrors.push({
      position: employeesIndexes.map((index) => ({ row: index, column: EMPLOYEE_COLUMNS.managerOfTeam })),
      message: 'multipleEmployeesWithSameManager',
      messageObject: { name: team.name },
      tab: ImportTab.EMPLOYEES,
    });
  });

  teams.forEach((team, index) => {
    if (containsSemicolon(team.name)) {
      validationErrors.push({
        position: [
          {
            row: index,
            column: TEAM_COLUMNS.name,
          },
        ],
        message: 'semicolonInTeamName',
        messageObject: { name: team.name },
        tab: ImportTab.TEAMS,
      });
    }
    if (containsSemicolon(team.id)) {
      validationErrors.push({
        position: [
          {
            row: index,
            column: TEAM_COLUMNS.id,
          },
        ],
        message: 'semicolonInTeamId',
        messageObject: { id: team.id },
        tab: ImportTab.TEAMS,
      });
    }
    if (team.name === '' && team.id) {
      validationErrors.push({
        position: [
          {
            row: index,
            column: TEAM_COLUMNS.name,
          },
        ],
        message: 'emptyField',
        tab: ImportTab.TEAMS,
      });
    } else if (team.name === '' && team.supervisorTeam) {
      validationErrors.push({
        position: [
          {
            row: index,
            column: TEAM_COLUMNS.name,
          },
        ],
        message: 'emptyField',
        tab: ImportTab.TEAMS,
      });
    }
    if (team.supervisorTeam && !teams.find((t) => t.name === team.supervisorTeam || t.id === team.supervisorTeam)) {
      validationErrors.push({
        position: [{ row: index, column: TEAM_COLUMNS.supervisorTeam }],
        message: 'supervisorTeamNotFound',
        messageObject: { name: team.supervisorTeam },
        tab: ImportTab.TEAMS,
      });
    }
    if (team.id && teams.some((t, i2) => t.id === team.id && i2 !== index)) {
      validationErrors.push({
        position: [{ row: index, column: TEAM_COLUMNS.id }],
        message: 'duplicitTeamIds',
        messageObject: { id: team.id },
        tab: ImportTab.TEAMS,
      });
    }
  });

  const cycles = findCycleEndpointsWithIndices(teams);

  if (cycles?.length) {
    cycles.forEach((cycle) => {
      validationErrors.push({
        position: [
          { row: cycle.first.index, column: TEAM_COLUMNS.name },
          { row: cycle.last.index, column: TEAM_COLUMNS.name },
        ],
        message: 'cycleDetected',
        messageObject: { name1: cycle.first.name, name2: cycle.last.name },
        tab: ImportTab.TEAMS,
      });
    });
  }

  return validationErrors;
};

const containsSemicolon = (value: string | null | undefined) => {
  return value && value.includes(';');
};
